import React from "react";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, Wrench, Heart, RefreshCw, MapPin, Phone, Mail } from "lucide-react";
import PageHero from "../components/PageHero";

const STATS = [
  { v: "25+", l: "Years in Gaming" },
  { v: "10K+", l: "Happy Gamers" },
  { v: "4.9★", l: "Google Rating" },
  { v: "Sony", l: "Authorised" },
];

const PILLARS = [
  { i: ShieldCheck, t: "Sony India Authorised", d: "Official warranty support, genuine accessories, factory-sealed consoles. Verified on Sony India's store locator." },
  { i: Wrench, t: "In-House Repairs", d: "Our expert technicians fix PS5, Xbox, Switch and more — fast turnaround, no shipping out, handled right here in-store." },
  { i: RefreshCw, t: "Best Trade-In Deals", d: "Upgrade without the full price tag. Get top value for your old consoles and games, instantly credited toward your next purchase." },
  { i: Heart, t: "By Gamers, For Gamers", d: "Our team plays everything we sell. Real recommendations from people who actually game." },
];

export default function AboutPage() {
  return (
    <div>
      <Helmet>
        <title>{"About Us — GamesTrade | 25+ Years of Gaming | Noida"}</title>
        <meta name="description" content="GamesTrade has been India's trusted gaming destination for 25+ years. Sony Authorised dealer at Spectrum Mall, Sector 75, Noida. Gaming expertise you can count on." />
        <link rel="canonical" href="https://gamestrade.in/about" />
        <meta property="og:title" content="About GamesTrade — 25+ Years of Gaming" />
        <meta property="og:description" content="Sony Authorised gaming store with 25+ years of expertise. New, used, pre-book. Spectrum Mall, Sector 75, Noida." />
        <meta property="og:url" content="https://gamestrade.in/about" />
        <meta property="og:type" content="website" />
      </Helmet>

      <PageHero
        eyebrow="About"
        title={<>25+ years, <br /><span className="text-gold">one obsession.</span></>}
        description="For 25+ years, GamesTrade has helped players buy, sell, trade and repair their gear. Today, we're one of India's most trusted homes for gaming."
        breadcrumbs={[{ to: "/", label: "Home" }, { label: "About" }]}
      />

      {/* Stats strip */}
      <section className="border-b border-gold/10">
        <div className="site-container py-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-4xl sm:text-5xl font-black text-gold tracking-tighter text-glow-yellow">{s.v}</div>
              <div className="text-[10px] mt-1 uppercase tracking-[0.25em] text-slate-400">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="site-container py-16">
        {/* Pillars */}
        <h2 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase mb-8">What we stand for</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {PILLARS.map((b, i) => (
            <div key={i} className="bg-navy-700 rounded-2xl p-6 neon-border">
              <b.i className="w-8 h-8 text-gold mb-3" />
              <h3 className="text-lg font-extrabold uppercase tracking-tight">{b.t}</h3>
              <p className="text-sm text-slate-300 mt-2 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>

        {/* Visit us */}
        <div className="bg-gradient-to-br from-navy-700 to-navy-900 border border-gold/30 rounded-2xl p-8 lg:p-10 neon-border">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Visit Us</div>
          <h3 className="text-2xl sm:text-3xl font-black tracking-tighter uppercase mb-6">Drop by the store.</h3>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="flex items-start gap-3 text-sm text-slate-300">
              <MapPin className="w-5 h-5 text-gold shrink-0 mt-0.5" />
              <span>Spectrum Mall LG-109,<br />Sector 75, Noida</span>
            </div>
            <a href="tel:+918287799290" className="flex items-start gap-3 text-sm text-slate-300 hover:text-gold transition">
              <Phone className="w-5 h-5 text-gold shrink-0 mt-0.5" />
              <span>+91 82877 99290<br /><span className="text-xs text-slate-500">10 AM – 9 PM, all days</span></span>
            </a>
            <a href="mailto:gamestradenoida@gmail.com" className="flex items-start gap-3 text-sm text-slate-300 hover:text-gold transition min-w-0">
              <Mail className="w-5 h-5 text-gold shrink-0 mt-0.5" />
              <span className="break-all">gamestradenoida@gmail.com</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
